import React, {
  createContext,
  FC,
  useContext,
  useEffect,
  useState,
} from 'react';
import useRestClient from '~/hooks/useRestClient';

interface SiteContextType extends Record<string, any> {
  theme: {
    borderRadius: number;
    color1: string;
    color2: string;
    color3: string;
    faviconSrc: string | null;
    fontBody: string;
    fontHeading: string;
    iconSet: string;
    productCardAspectRatio: string;
    productImageCrop: boolean;
    productPageRelatedProducts: boolean;
  };
}

const SiteContext = createContext<SiteContextType>({} as SiteContextType);

type ShopInfo = {
  shopInfo: {
    allowCartRemoval: boolean;
    allowCartUpdate: boolean;
    appStoreUrls: {
      android: string;
      apple: string;
    };
    appleAppArgument: string | null;
    appleId: string;
    contactEmail: string;
    description: string;
    facebookPixelId: string;
    featureFlags: {
      lotwReplaysEnabled: boolean;
    };
    googleAnalyticsUid: string;
    csGoogleAnalyticsUid: string;
    hideAvailableStockCount: boolean;
    homepageDescription: string;
    homepageTitle: string;
    id: string;
    name: string;
    payPalDetails: {
      clientId: string;
      merchantId: string;
      bnCode: string;
    } | null;
    paymentMethods: string[];
    pinterestPixelId: string;
    sezzlePublicKey: string;
    shopLogo: string;
    soldKeyword: string;
    tikTokPixelId: string;
  };
};

const initialShopInfo: ShopInfo = {
  shopInfo: {
    allowCartRemoval: false,
    allowCartUpdate: false,
    appStoreUrls: { android: '', apple: '' },
    appleAppArgument: null,
    appleId: '',
    contactEmail: '',
    description: '',
    facebookPixelId: '',
    featureFlags: {
      lotwReplaysEnabled: true,
    },
    googleAnalyticsUid: '',
    csGoogleAnalyticsUid: '',
    hideAvailableStockCount: false,
    homepageDescription: '',
    homepageTitle: '',
    id: '',
    name: '',
    payPalDetails: null,
    paymentMethods: [],
    pinterestPixelId: '',
    sezzlePublicKey: '',
    shopLogo: '',
    soldKeyword: '',
    tikTokPixelId: '',
  },
};

const SiteProvider: FC<{ config: any }> = ({ config, children }) => {
  const [shopInfo, setShopInfo] = useState<ShopInfo>(initialShopInfo);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isResolved, setIsResolved] = useState<boolean>(false);
  const [isRejected, setIsRejected] = useState<boolean>(false);

  const restClient = useRestClient();

  useEffect(() => {
    const fetchData = async () => {
      const res = await restClient.getShopInfo().finally(() => {
        setIsLoading(false);
      });
      if (!res.data) {
        setIsRejected(true);
      }
      setShopInfo(res.data.data);
      setIsResolved(true);
    };

    // noinspection JSIgnoredPromiseFromCall
    fetchData();
  }, [restClient, setShopInfo]);

  return (
    <SiteContext.Provider
      value={{
        ...shopInfo,
        setShopInfo,
        theme: config?.theme,
        mailingListPopup: config?.mailingListPopup,
        header: config?.header,
        footer: config?.footer,
        homePage: config?.homePage,
        globals: shopInfo,
        loadingGlobals: isLoading,
        resolvedGlobals: isResolved,
        rejectedGlobals: isRejected,
      }}
    >
      {children}
    </SiteContext.Provider>
  );
};

function useSiteContext() {
  return useContext(SiteContext);
}

export { SiteProvider, useSiteContext };
